import React, { useState, useEffect } from 'react';
import { makeApiRequest } from '../../../utils/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Button, Modal, Box, TextField, Typography, Alert, Grid, TablePagination, CircularProgress,
    Select, MenuItem, FormControl, InputLabel, InputAdornment, Autocomplete
} from '@mui/material';
import { styled, width } from '@mui/system';
import { Search } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(1),
    textAlign: 'center',
    '&:first-child': {
        width: '5%',
    },
}));

const StyledTableRow = styled(TableRow)(() => ({}));

const Chapter = () => {
    const [chapterData, setChapterData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(null);
    const [editedChapterName, setEditedChapterName] = useState('');
    const [statusChangeModalOpen, setStatusChangeModalOpen] = useState(false);
    const [statusToUpdate, setStatusToUpdate] = useState('');
    const [status, setStatus] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentModuleName, setCurrentModuleName] = useState('');
    const [originalChapterName, setOriginalChapterName] = useState('');
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    const [currentItems, setCurrentItems] = useState([]);
    const [modules, setModules] = useState([]);
    const [selectedModule, setSelectedModule] = useState('');
    const [newChapterName, setNewChapterName] = useState('');
    const [allChapterData, setAllChapterData] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [allData, setAllData] = useState([]);

    const handleAddChapter = async () => {
        if (!selectedModule || !newChapterName) {
            toast.warn('Please fill in all fields');
            return;
        }

        setNewChapterName('');

        try {
            const response = await makeApiRequest('post', '/api/chapter/add', {
                token: localStorage.getItem('authToken'),
                module_id: selectedModule,
                chapter_name: newChapterName,
                chapter_status: status,
                userid: localStorage.getItem('userid'),
            });

            if (response.status === 'success') {
                fetchChapterData(page + 1, rowsPerPage);
                setSelectedModule('');
                setNewChapterName('');
                toast.success('Chapter added successfully');
            } else {
                throw new Error('Failed to add chapter');
            }
        } catch (err) {
            console.error('API Error:', err);
            toast.error(err.response?.data?.message);
        }
    };

    const fetchChapterData = async (pageNumber, itemsPerPage) => {
        setLoading(true);
        setError(null);

        try {
            const requestData = {
                token: localStorage.getItem('authToken'),
                page: pageNumber,
                per_page: itemsPerPage,
            };
            const response = await makeApiRequest('post', '/api/chapter/list', requestData);
            if (response.status === 'success' && response.data) {
                setChapterData(response.data.data);
                setCurrentItems(response.data.data);
                if (response.data.pagination) {
                    setTotalItems(response.data.pagination.total);
                    setPage(response.data.pagination.current_page - 1);
                }
            } else {
                throw new Error('Could not fetch chapter data');
            }
        } catch (error) {
            console.error('Error:', error);
            setError('Failed to load chapter data. Please try again.');
        } finally {
            setLoading(false);
        }
    };


    const fetchModules = async () => {
        try {
            const response = await makeApiRequest('post', '/api/module/list', {
                token: localStorage.getItem('authToken'),
                per_page: 100  // Set a reasonable default instead of null
            });

            if (response.status === 'success' && response.data) {
                setModules(response.data.data);
            } else {
                throw new Error("Failed to fetch modules");

            }
        } catch (error) {
            console.error("Error fetching modules:", error);
            toast.error("Failed to load modules.");
        }
    };


    useEffect(() => {
        fetchModules();
    }, []);

    const fetchAllData = async () => {
        try {
            const response = await makeApiRequest('post', '/api/chapter/list', {
                token: localStorage.getItem('authToken'),
                per_page: null
            });

            if (response.status === 'success' && response.data) {
                setAllData(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching all data:', err);
        }
    };

    useEffect(() => {
        fetchAllData();
    }, []);

    useEffect(() => {
        fetchChapterData(page + 1, rowsPerPage);
        // fetchAllData();
    }, [page, rowsPerPage]);

    const handleSearchChange = async (e) => {
        const searchValue = e.target.value?.toLowerCase();
        setSearchTerm(searchValue);

        if (searchValue === '') {
            fetchChapterData(1, rowsPerPage);
            return;
        }

        const filteredResults = allData.filter(item =>
            item.module?.toLowerCase().includes(searchValue) ||
            item.chapter?.toLowerCase().includes(searchValue)
        );

        setCurrentItems(filteredResults.slice(0, rowsPerPage));
        setTotalItems(filteredResults?.length);
        setPage(0);
    };


    const handleEditClick = (index) => {
        setCurrentIndex(index);
        const chapterName = currentItems[index].chapter;
        setEditedChapterName(chapterName);
        setOriginalChapterName(chapterName);
        setCurrentModuleName(currentItems[index].module);
        setIsSaveDisabled(true);
        setOpen(true);
    };

    const handleChapterChange = (e) => {
        const updatedName = e.target.value;
        setEditedChapterName(updatedName);
        setIsSaveDisabled(updatedName === originalChapterName);
    };

    const handleStatusChangeClick = (index, currentStatus) => {
        setCurrentIndex(index);
        setStatusToUpdate(currentStatus);
        setStatusChangeModalOpen(true);
    };

    const handleSaveEdit = async () => {
        try {
            const response = await makeApiRequest('post', '/api/chapter/edit', {
                token: localStorage.getItem('authToken'),
                chapter_id: currentItems[currentIndex].chapter_id,
                updated_chapter: editedChapterName
            });

            if (response.status === 'success') {
                fetchChapterData(page + 1, rowsPerPage);
                setOpen(false);
                toast.success('Chapter updated successfully');
            } else {
                throw new Error('Failed to update chapter');
            }
        } catch (err) {
            console.error('API Error:', err);
            toast.error('Error updating chapter');
        }
    };

    const handleStatusConfirm = async () => {
        try {
            const response = await makeApiRequest('post', '/api/chapter/status', {
                token: localStorage.getItem('authToken'),
                chapter_id: currentItems[currentIndex].chapter_id,
                status: statusToUpdate === 0 ? 1 : 0
            });

            if (response.status === 'success') {
                fetchChapterData(page + 1, rowsPerPage);
                setStatusChangeModalOpen(false);
            } else {
                throw new Error('Failed to update status');
            }
        } catch (err) {
            console.error('API Error:', err);
            toast.error('Error updating status');
        }
    };

    const handleChangePage = (event, newPage) => {
        if (searchTerm) {
            const startIndex = newPage * rowsPerPage;
            const filteredResults = allData.filter(item =>
                item.module.toLowerCase().includes(searchTerm?.toLowerCase()) ||
                item.chapter.toLowerCase().includes(searchTerm?.toLowerCase())
            );
            setCurrentItems(filteredResults.slice(startIndex, startIndex + rowsPerPage));
            setPage(newPage);
        } else {
            setPage(newPage);
            fetchChapterData(newPage + 1, rowsPerPage);
        }
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        if (searchTerm) {
            const filteredResults = allData.filter(item =>
                item.module?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
                item.chapter?.toLowerCase().includes(searchTerm?.toLowerCase())
            );
            setCurrentItems(filteredResults.slice(0, newRowsPerPage));
        } else {
            fetchChapterData(1, newRowsPerPage);
        }
    };

    return (
        <Box sx={{ height: "auto", backgroundColor: "#ECEDFF" }}>
            <ToastContainer
                position="top-center"
                autoClose={2000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick={false}
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover={false}
                theme="light"
            />
            <Grid container sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Grid item xs={11}>
                    <Box container sx={{ height: "80px", display: "flex", justifyContent: "start", alignItems: "center", backgroundColor: "#ECEDFF" }}>
                        <Typography variant="h4" sx={{
                            color: "#481E82", fontWeight: "500", fontFamily: 'Roboto'
                        }}> Add Chapter</Typography>
                    </Box>

                    <Grid
                        item
                        xs={12}
                        sx={{
                            backgroundColor: 'white',
                            padding: { xs: "10px", md: "20px" },
                            borderRadius: "5px",
                            marginBottom: "20px",
                            boxShadow: " 0px 1px 2px rgba(0, 0, 0, 0.2);"
                        }}
                    >

                        <Box
                            sx={{
                                // display: "flex",
                                // flexDirection: { xs: "column", md: "row" },
                                // alignItems: "center",
                                // justifyContent: "space-between",
                                // gap: { xs: "15px", md: "0" }
                            }}
                        >
                            <Grid container sx={{ justifyContent: "space-between" }}>
                                <Grid item xs={12} sm={7}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={6} lg={5}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Select Module</InputLabel>
                                                <Select
                                                    value={selectedModule}
                                                    onChange={(e) => setSelectedModule(e.target.value)}
                                                    label="Select Module"
                                                >
                                                    {modules.map((module, index) => (
                                                        <MenuItem key={index} value={module.module_id}>
                                                            {module.module}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                        </Grid>
                                        <Grid item xs={12} sm={6} md={6} lg={5}>
                                            <TextField
                                                placeholder="Enter Chapter Name"
                                                value={newChapterName}
                                                onChange={(e) => setNewChapterName(e.target.value)}
                                                defaultValue="Small"
                                                size="small"
                                                sx={{
                                                    width: "100%"
                                                }}
                                            />
                                        </Grid>
                                    </Grid>

                                </Grid>
                                <Grid item xs={12} sm={5} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleAddChapter}
                                        size='medium'
                                        sx={{
                                            // height: { xs: '45px', md: '50px' },
                                            // width: { xs: "100%", md: "auto" },
                                            marginTop: { xs: "10px", sm: "0" }
                                        }}
                                    >
                                        Add Chapter
                                    </Button>
                                </Grid>
                            </Grid>

                            {/* </Box> */}


                        </Box>
                    </Grid>
                    <Box container sx={{ height: "80px", display: "flex", justifyContent: "start", alignItems: "center", backgroundColor: "#ECEDFF" }}>
                        <Typography variant="h4" sx={{
                            color: "#481E82", fontWeight: "500", fontFamily: 'Roboto'
                        }}> All Chapters</Typography>
                    </Box>
                    <Grid item xs={12} sx={{ backgroundColor: 'white', padding: "20px", borderRadius: "5px 5px 0 0" }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Box sx={{ /* width: { xs: "100%", sm: "25%" } */ display: "flex", alignItems: "center" }}>
                                    <TextField
                                        id="search-box"
                                        variant="outlined"
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        placeholder='Search Module'
                                        defaultValue="Small"
                                        size="small"
                                        sx={{
                                            // marginLeft: "10px",
                                            // marginRight: "10px",
                                            width: "100%"
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>

                    </Grid>
                    <Box>
                        {loading && (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                                <CircularProgress size={40} thickness={4} sx={{ color: '#481E82' }} />
                            </Box>
                        )}

                        {/* {error && <Alert severity="error">{error}</Alert>} */}

                        {!loading && !error && currentItems?.length === 0 && (
                            <Typography variant="h6" color="error" sx={{ textAlign: 'center' }}>
                                No data matching the search term.
                            </Typography>
                        )}

                        {!loading && !error && currentItems?.length > 0 && (
                            <>
                                <TableContainer component={Paper} sx={{ borderRadius: "0px" }}>
                                    <Table sx={{ minWidth: 650, borderRadius: "0px" }} aria-label="chapter table">
                                        <TableHead sx={{ backgroundColor: '#ECEDFF', height: "60px", marginTop: "0px", margin: "0px", borderRadius: "0px" }}>
                                            <TableRow sx={{ borderRadius: "0px" }}>
                                                <StyledTableCell sx={{ color: 'black', fontSize: "14px", fontFamily: "Roboto" }}><strong>S/No.</strong></StyledTableCell>
                                                <StyledTableCell sx={{ color: 'black', fontSize: "14px", fontFamily: "Roboto", textAlign: "start" }}><strong>Module Name</strong></StyledTableCell>
                                                <StyledTableCell sx={{ color: 'black', fontSize: "14px", textAlign: "start", fontFamily: "Roboto" }}><strong>Chapter Name</strong></StyledTableCell>
                                                <StyledTableCell sx={{ color: 'black', fontSize: "14px", fontFamily: "Roboto" }}><strong>Status</strong></StyledTableCell>
                                                <StyledTableCell sx={{ color: 'black', fontSize: "14px", fontFamily: "Roboto" }}><strong>Action</strong></StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {currentItems.map((item, index) => (
                                                <StyledTableRow key={index}>
                                                    <StyledTableCell sx={{ fontWeight: 500, fontSize: "14px" }}>
                                                        {index + 1 + page * rowsPerPage}
                                                    </StyledTableCell>
                                                    <StyledTableCell sx={{ fontWeight: 500, fontSize: "14px", color: "black", textAlign: "start" }}>
                                                        {item.module}
                                                    </StyledTableCell>
                                                    <StyledTableCell sx={{ fontWeight: 500, fontSize: "14px", color: "black", textAlign: "start" }}>
                                                        {item.chapter}
                                                    </StyledTableCell>
                                                    <StyledTableCell>
                                                        <Button
                                                            size='small'
                                                            variant="contained"
                                                            onClick={(e) => handleStatusChangeClick(index, item.status, e)}
                                                            sx={item.status === 0 ? {
                                                                bgcolor: '#CBFAF0',
                                                                color: '#298880',
                                                                // height: 24,
                                                                minWidth: 80,
                                                                borderRadius: '4px',
                                                                border: "1px solid #98EEDB",
                                                                textTransform: 'none',
                                                                // fontSize: '0.75rem',
                                                                boxShadow: "none",
                                                                // padding: '2px 8px',
                                                                '&:hover': { bgcolor: '#B3E5D8' },
                                                                width: "100px"
                                                            } : {
                                                                bgcolor: '#FDE6EB',
                                                                color: '#BA3068',
                                                                // height: 24,
                                                                minWidth: 80,
                                                                borderRadius: '4px',
                                                                border: "1px solid #FCD6DE",
                                                                textTransform: 'none',
                                                                // fontSize: '0.75rem',
                                                                boxShadow: "none",
                                                                // padding: '2px 8px',
                                                                '&:hover': { bgcolor: '#F8C8D4' },
                                                                width: "100px"
                                                            }}
                                                        >
                                                            {item.status === 0 ? "Active" : "Inactive"}
                                                        </Button>
                                                    </StyledTableCell>
                                                    <StyledTableCell>
                                                        <Button
                                                            variant="outlined"
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => handleEditClick(index)}
                                                        >
                                                            <EditIcon sx={{ mr: 1 }} /> Edit
                                                        </Button>
                                                    </StyledTableCell>
                                                </StyledTableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    rowsPerPageOptions={[10, 20, 50]}
                                    component="div"
                                    count={totalItems}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </>
                        )}
                    </Box>
                </Grid>
            </Grid>
            <Modal
                open={statusChangeModalOpen}
                onClose={() => setStatusChangeModalOpen(false)}
                aria-labelledby="modal-status-change-title"
            >
                <Box sx={{ position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', width: 400, backgroundColor: 'white', borderRadius: 5, padding: 4, boxShadow: 24 }}>
                    <Typography variant="h6" id="modal-status-change-title">
                        Change Status to {statusToUpdate === 0 ? 'Inactive' : 'Active'}?
                    </Typography>
                    <Button variant="contained" color="primary" onClick={handleStatusConfirm} sx={{ marginRight: 2 }}>Confirm</Button>
                    <Button variant="outlined" color="secondary" onClick={() => setStatusChangeModalOpen(false)}>Cancel</Button>
                </Box>
            </Modal>
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                aria-labelledby="modal-edit-title"
            >
                <Box sx={{ position: 'absolute', top: 100, left: '50%', transform: 'translateX(-50%)', width: 400, backgroundColor: 'white', borderRadius: 5, padding: 4, boxShadow: 24 }}>
                    <Typography variant="h6" id="modal-edit-title">
                        Edit Chapter
                    </Typography>
                    <TextField
                        fullWidth
                        label="Module Name"
                        variant="outlined"
                        disabled={true}
                        value={currentModuleName}
                        sx={{ marginBottom: 2, marginTop: "20px" }}
                    />
                    <TextField
                        fullWidth
                        label="Chapter Name"
                        variant="outlined"
                        value={editedChapterName}
                        onChange={handleChapterChange}
                        sx={{ marginBottom: 2, marginTop: "20px" }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveEdit}
                        sx={{ marginRight: 2 }}
                        disabled={isSaveDisabled}
                    >
                        Save
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={() => setOpen(false)}>Cancel</Button>
                </Box>
            </Modal>
        </Box >
    );
};

export default Chapter;